export interface ItemDescription {
  amount: number
  price_per_unit: number
  value: number
}

export type FareItemKey =
  | 'peak_consumption'
  | 'off_peak_consumption'
  | 'measured_peak_demand'
  | 'measured_off_peak_demand'
  | 'contracted_peak_demand'
  | 'contracted_off_peak_demand'
  | 'exceeded_reactive_peak_demand'
  | 'exceeded_reactive_off_peak_demand'
  | 'peak_reactive'
  | 'off_peak_reactive'
  | 'unused_peak_demand'
  | 'unused_off_peak_demand'
  | 'exceeded_peak_demand'
  | 'exceeded_off_peak_demand'
  | 'tudsg_demand'
  | 'credit_value'
  | 'debit_value'
  | 'relative_emission'
  | 'public_light_bill'
  | 'covid_bill'
  | 'hydrical_scarcity_covid_bill'
  | 'tariff_total'
  | string

export type FareItems = Record<FareItemKey, ItemDescription>

export const createEmptyItemDescription = (): ItemDescription => ({
  amount: 0,
  price_per_unit: 0,
  value: 0,
})

export const createFareItems = (customItems?: Partial<FareItems>): FareItems => {
  const defaultItems: FareItems = {
    peak_consumption: createEmptyItemDescription(),
    off_peak_consumption: createEmptyItemDescription(),
    measured_peak_demand: createEmptyItemDescription(),
    measured_off_peak_demand: createEmptyItemDescription(),
    contracted_peak_demand: createEmptyItemDescription(),
    contracted_off_peak_demand: createEmptyItemDescription(),
    exceeded_reactive_peak_demand: createEmptyItemDescription(),
    exceeded_reactive_off_peak_demand: createEmptyItemDescription(),
    peak_reactive: createEmptyItemDescription(),
    off_peak_reactive: createEmptyItemDescription(),
    unused_peak_demand: createEmptyItemDescription(),
    unused_off_peak_demand: createEmptyItemDescription(),
    exceeded_peak_demand: createEmptyItemDescription(),
    exceeded_off_peak_demand: createEmptyItemDescription(),
    tudsg_demand: createEmptyItemDescription(),
    credit_value: createEmptyItemDescription(),
    debit_value: createEmptyItemDescription(),
    relative_emission: createEmptyItemDescription(),
    public_light_bill: createEmptyItemDescription(),
    covid_bill: createEmptyItemDescription(),
    hydrical_scarcity_covid_bill: createEmptyItemDescription(),
    tariff_total: createEmptyItemDescription(),
  }

  return { ...defaultItems, ...customItems }
}

export interface IFareAnalysisParams {
  contracted_peak_demand: number
  contracted_off_peak_demand: number
  peak_demand: number
  off_peak_demand: number
  peak_consumption: number
  off_peak_consumption: number
}

export const addFareItem = (fareItems: FareItems, key: FareItemKey, itemDescription: ItemDescription): FareItems => {
  return {
    ...fareItems,
    [key]: itemDescription,
  }
}

export interface HistoricalDataItem {
  month: string
  peak_invoiced_consumption: number
  peak_measured_demand: number
  intermediate_consumption: number
  off_peak_invoiced_consumption: number
  off_peak_measured_demand: number
  peak_reactive_energy: number
  peak_reactive_demand: number
  off_peak_reactive_energy: number
  off_peak_reactive_demand: number
  reserved_consumption: number
  reserved_reactive_energy: number
  tusdg_measured_demand: number
}

export const createHistoricalDataItem = (customItems?: Partial<HistoricalDataItem>): HistoricalDataItem => {
  const defaultItems: HistoricalDataItem = {
    month: '',
    peak_invoiced_consumption: 0,
    peak_measured_demand: 0,
    intermediate_consumption: 0,
    off_peak_invoiced_consumption: 0,
    off_peak_measured_demand: 0,
    peak_reactive_energy: 0,
    peak_reactive_demand: 0,
    off_peak_reactive_energy: 0,
    off_peak_reactive_demand: 0,
    reserved_consumption: 0,
    reserved_reactive_energy: 0,
    tusdg_measured_demand: 0,
  }

  return { ...defaultItems, ...customItems }
}

export const createHistoricalDataList = (items: any[]): HistoricalDataItem[] => {
  if (!Array.isArray(items)) return []
  return items
    .filter((item) => item.month && item.month.trim() !== '')
    .map((item) =>
      createHistoricalDataItem({
        month: item.month,
        peak_invoiced_consumption: Number(item.peak_invoiced_consumption) || 0,
        peak_measured_demand: Number(item.peak_measured_demand) || 0,
        intermediate_consumption: Number(item.intermediate_consumption) || 0,
        off_peak_invoiced_consumption: Number(item.off_peak_invoiced_consumption) || 0,
        off_peak_measured_demand: Number(item.off_peak_measured_demand) || 0,
        peak_reactive_energy: Number(item.peak_reactive_energy) || 0,
        peak_reactive_demand: Number(item.peak_reactive_demand) || 0,
        off_peak_reactive_energy: Number(item.off_peak_reactive_energy) || 0,
        off_peak_reactive_demand: Number(item.off_peak_reactive_demand) || 0,
        reserved_consumption: Number(item.reserved_consumption) || 0,
        reserved_reactive_energy: Number(item.reserved_reactive_energy) || 0,
        tusdg_measured_demand: Number(item.tusdg_measured_demand) || 0,
      }),
    )
}
